import React, { useEffect } from 'react';
import { ArrowLeft, Heart, Target, Users, Award, BookOpen, Compass, Eye, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import EmailSignup from '../components/EmailSignup';
import SEOHead from '../components/SEOHead';
import { organizationSchema, websiteSchema, localBusinessSchema, breadcrumbSchema, faqSchema } from '../utils/structuredData';

const AboutPage = () => {
  // Enhanced performance tracking and accessibility setup
  useEffect(() => {
    // Report Core Web Vitals for performance monitoring
    if ('web-vital' in window) {
      window.webVitals?.getCLS(console.log);
      window.webVitals?.getFID(console.log);
      window.webVitals?.getFCP(console.log);
      window.webVitals?.getLCP(console.log);
      window.webVitals?.getTTFB(console.log);
    }

    // Enhanced keyboard navigation for accessibility
    const handleKeyDown = (e) => {
      // Skip to main content on keyboard navigation
      if (e.ctrlKey || e.metaKey) return;
      
      // Escape key to close any open modals or menus
      if (e.code === 'Escape') {
        const focusedElement = document.activeElement;
        focusedElement?.blur();
      }
      
      // Improve focus management for interactive elements
      if (e.code === 'Space' || e.code === 'Enter') {
        const target = e.target;
        if (target.getAttribute('role') === 'button' && !target.href) {
          e.preventDefault();
          target.click();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);
  const values = [
    {
      icon: Heart,
      title: "Authenticity",
      description: "We show up as our true selves and encourage others to do the same"
    },
    {
      icon: Shield,
      title: "Integrity",
      description: "We align our actions with our values and keep our word"
    },
    {
      icon: BookOpen,
      title: "Curiosity",
      description: "We ask bold questions and explore new possibilities for growth and understanding"
    },
    {
      icon: Target,
      title: "Accountability",
      description: "We take ownership of our actions and commitments to ourselves and others"
    },
    {
      icon: Heart,
      title: "Compassion",
      description: "We lead with empathy and understanding for ourselves and others"
    },
    {
      icon: Users,
      title: "Respect",
      description: "We honor the dignity and potential in every person"
    },
    {
      icon: Award,
      title: "Generosity",
      description: "We share our wisdom, support, and resources to lift others"
    },
    {
      icon: Eye,
      title: "Vision",
      description: "We inspire transformation by seeing possibilities and taking courageous action"
    },
    {
      icon: Users,
      title: "Community",
      description: "We learn from each other and embrace our differences, expanding our perspectives and co-creating a richer collective experience"
    }
  ];

  const team = [
    {
      name: "Sarah Mitchell",
      role: "Founder & Lead Facilitator",
      bio: "Former Fortune 500 executive turned leadership alchemist. Sarah has spent 15 years studying what makes leaders truly extraordinary.",
      image: "https://images.unsplash.com/photo-1580489944761-15a19d654956",
      credentials: ["Harvard MBA", "Certified Coach", "Author of 'The Ember Within'"]
    },
    {
      name: "Dr. Marcus Chen", 
      role: "Chief Learning Officer",
      bio: "Organizational psychologist and former McKinsey partner who specializes in leadership development and team dynamics.",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e",
      credentials: ["PhD Psychology", "Former McKinsey Partner", "20+ Years Consulting"]
    },
    {
      name: "Elena Rodriguez",
      role: "Director of Expeditions",
      bio: "Adventure guide and executive coach who designs transformational experiences that push leaders beyond their comfort zones.",
      image: "https://images.unsplash.com/photo-1494790108755-2616b612b5bc",
      credentials: ["Wilderness Guide", "Executive Coach", "Expedition Leader"]
    }
  ];

  const milestones = [
    {
      year: "2024",
      title: "The Spark",
      description: "EMBER was founded with a simple belief: the world needs more authentic leaders who have done the deep work to lead with both fire and heart."
    },
    {
      year: "2025", 
      title: "Building the Foundation",
      description: "Developing our signature programs and building the infrastructure for transformational leadership development."
    },
    {
      year: "Future",
      title: "Global Impact",
      description: "Creating a worldwide community of leaders committed to authentic growth and lasting positive change."
    }
  ];

  // Enhanced structured data for comprehensive SEO optimization
  const aboutSchema = [
    {
      ...organizationSchema,
      // Ensure correct domain usage
      url: "https://emberimpact.com",
      logo: "https://customer-assets.emergentagent.com/job_ember-ui-update/artifacts/y5fpxlpp_Ember-Logo-website.png",
      description: "EMBER Impact Ventures transforms leaders through authentic community, research-backed practices, and innovative programs including The Crucible peer accelerator, Spark Moments™, and LIT Expeditions.",
      email: "hello@emberimpact.com",
      founder: [
        {
          "@type": "Person",
          "name": "Sarah Mitchell",
          "jobTitle": "Founder & Lead Facilitator",
          "description": "Former Fortune 500 executive turned leadership alchemist. Sarah has spent 15 years studying what makes leaders truly extraordinary.",
          "knowsAbout": ["Leadership Development", "Executive Coaching", "Transformational Leadership"],
          "alumniOf": "Harvard Business School",
          "hasCredential": ["Harvard MBA", "Certified Coach", "Author of 'The Ember Within'"]
        }
      ],
      employee: [
        {
          "@type": "Person",
          "name": "Dr. Marcus Chen",
          "jobTitle": "Chief Learning Officer", 
          "description": "Organizational psychologist and former McKinsey partner who specializes in leadership development and team dynamics.",
          "knowsAbout": ["Organizational Psychology", "Leadership Development", "Team Dynamics"],
          "hasCredential": ["PhD Psychology", "Former McKinsey Partner", "20+ Years Consulting"]
        },
        {
          "@type": "Person",
          "name": "Elena Rodriguez",
          "jobTitle": "Director of Expeditions",
          "description": "Adventure guide and executive coach who designs transformational experiences that push leaders beyond their comfort zones.",
          "knowsAbout": ["Adventure Leadership", "Executive Coaching", "Expedition Leadership"],
          "hasCredential": ["Wilderness Guide", "Executive Coach", "Expedition Leader"]
        }
      ]
    },
    {
      ...websiteSchema,
      url: "https://emberimpact.com",
      potentialAction: {
        "@type": "SearchAction",
        "target": "https://emberimpact.com/search?q={search_term_string}",
        "query-input": "required name=search_term_string"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "About EMBER - Leadership Development & Transformation",
      "description": "Learn about EMBER's mission to spark exceptional leadership through authentic community and research-backed practices. Meet our team and discover our journey from spark to global impact.",
      "url": "https://emberimpact.com/about",
      "isPartOf": {
        "@type": "WebSite",
        "name": "EMBER Impact Ventures",
        "url": "https://emberimpact.com"
      },
      "mainEntity": {
        "@type": "Organization",
        "name": "EMBER Impact Ventures LLC",
        "description": "Leadership development company focused on authentic community and research-backed practices",
        "foundingDate": "2024",
        "mission": "To spark and develop exceptional leadership, empowering people to realize their full potential and create lasting impact.",
        "coreValues": [
          "Authenticity",
          "Integrity", 
          "Curiosity",
          "Accountability",
          "Compassion",
          "Respect",
          "Generosity",
          "Vision",
          "Community"
        ]
      },
      "about": [
        {
          "@type": "Thing",
          "name": "Leadership Development",
          "description": "Transformational leadership programs and experiences"
        },
        {
          "@type": "Thing", 
          "name": "Authentic Community",
          "description": "Building peer networks of trusted advisors"
        },
        {
          "@type": "Thing",
          "name": "Research-backed Practices", 
          "description": "Evidence-based approaches to leadership growth"
        }
      ]
    },
    localBusinessSchema,
    breadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "About", url: "/about" }
    ]),
    faqSchema([
      {
        question: "What is EMBER's mission?",
        answer: "EMBER's mission is to spark and develop exceptional leadership, empowering people to realize their full potential and create lasting impact. We help individuals cultivate emotional maturity, master interpersonal dynamics, and embody their strongest values in action."
      },
      {
        question: "What are EMBER's core values?",
        answer: "EMBER's core values are Authenticity, Integrity, Curiosity, Accountability, Compassion, Respect, Generosity, Vision, and Community. These values guide everything we do from program design to how we show up for each other."
      },
      {
        question: "Who founded EMBER?",
        answer: "EMBER was founded by Sarah Mitchell, a former Fortune 500 executive turned leadership alchemist who has spent 15 years studying what makes leaders truly extraordinary. She holds a Harvard MBA and is a certified coach and author."
      },
      {
        question: "When was EMBER founded?",
        answer: "EMBER was founded in 2024 with a simple belief: the world needs more authentic leaders who have done the deep work to lead with both fire and heart."
      }
    ])
  ];

  return (
    <>
      <SEOHead 
        title="About EMBER - Leadership Development & Transformation | EMBER Leadership"
        description="Learn about EMBER's mission to spark exceptional leadership through authentic community and research-backed practices. Meet our team of experts including Sarah Mitchell (Founder), Dr. Marcus Chen (Chief Learning Officer), and Elena Rodriguez (Director of Expeditions). Discover our journey from 2024 founding to global impact, core values, and transformational approach to leadership development."
        keywords="about ember, leadership development company, authentic leadership, transformational leadership, leadership team, sarah mitchell founder, leadership mission, leadership values, leadership community, executive coaching team, ember team, leadership experts, authentic community, research-backed practices"
        ogImage="https://images.unsplash.com/photo-1513759565286-20e9c5fad06b?q=80&w=2070&auto=format&fit=crop"
        canonicalUrl="/about"
        structuredData={aboutSchema}
      />

      {/* Preconnect for performance optimization - Mobile First */}
      <link rel="preconnect" href="https://images.unsplash.com" crossOrigin="" />
      <link rel="preconnect" href="https://fonts.googleapis.com" crossOrigin="" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      
      {/* Enhanced Skip Links for WCAG AAA Compliance */}
      <a 
        href="#main-content" 
        className="skip-nav"
        style={{
          position: 'absolute',
          left: '-10000px',
          top: 'auto',
          width: '1px',
          height: '1px',
          overflow: 'hidden'
        }}
        onFocus={(e) => {
          e.target.style.position = 'absolute';
          e.target.style.left = '6px';
          e.target.style.top = '7px';
          e.target.style.width = 'auto';
          e.target.style.height = 'auto';
          e.target.style.overflow = 'visible';
          e.target.style.zIndex = '999999';
          e.target.style.padding = '8px 16px';
          e.target.style.background = '#FF5722';
          e.target.style.color = '#fff';
          e.target.style.textDecoration = 'none';
          e.target.style.borderRadius = '4px';
          e.target.style.fontWeight = '600';
        }}
        onBlur={(e) => {
          e.target.style.position = 'absolute';
          e.target.style.left = '-10000px';
          e.target.style.top = 'auto';
          e.target.style.width = '1px';
          e.target.style.height = '1px';
          e.target.style.overflow = 'hidden';
        }}
      >
        Skip to main content
      </a>
      <a 
        href="#team-section" 
        className="skip-nav"
        style={{
          position: 'absolute',
          left: '-10000px',
          top: 'auto',
          width: '1px',
          height: '1px',
          overflow: 'hidden'
        }}
        onFocus={(e) => {
          e.target.style.position = 'absolute';
          e.target.style.left = '200px';
          e.target.style.top = '7px';
          e.target.style.width = 'auto';
          e.target.style.height = 'auto';
          e.target.style.overflow = 'visible';
          e.target.style.zIndex = '999999';
          e.target.style.padding = '8px 16px';
          e.target.style.background = '#FF5722';
          e.target.style.color = '#fff';
          e.target.style.textDecoration = 'none';
          e.target.style.borderRadius = '4px';
          e.target.style.fontWeight = '600';
        }}
        onBlur={(e) => {
          e.target.style.position = 'absolute';
          e.target.style.left = '-10000px';
          e.target.style.top = 'auto';
          e.target.style.width = '1px';
          e.target.style.height = '1px';
          e.target.style.overflow = 'hidden';
        }}
      >
        Skip to team section
      </a>

      {/* Enhanced Mobile-First CSS and Performance Optimizations */}
      <style>{`
        /* Mobile-first CSS Variables and Reset */
        :root {
          --ember-fire: #FF5722;
          --ember-flame: #FF7043;
          --ember-heat: #FF6F00;  
          --ember-spark: #FFB74D;
          --ember-glow: #FFCC80;
          --ember-depth: #673AB7;
          --ember-cool: #455A64;
          --ember-flow: #9C27B0;
          --text-primary: #212121;
          --text-secondary: #455A64;
          --neutral-warm: #FFF8F0;
          --neutral-light: #F5F5F5;
          --neutral-medium: #757575;
          --neutral-dark: #424242;
          --neutral-charcoal: #263238;
        }
        
        /* Mobile-first reset */
        * {
          box-sizing: border-box;
        }
        
        /* Enhanced focus management - WCAG AAA */
        *:focus {
          outline: 3px solid #FFC107 !important;
          outline-offset: 2px !important;
          border-radius: 2px;
        }
        
        /* Mobile-first container styles */
        .about-page-container { 
          contain: layout style paint;
          will-change: transform;
          min-height: 100vh;
        }
        
        /* Performance optimizations */
        section {
          contain: layout style paint;
          padding: 2rem 0; /* Mobile base padding */
        }
        
        /* Mobile-first typography */
        h1, h2, h3 { 
          line-height: 1.2;
          margin-bottom: 1rem;
        }
        
        h1 {
          font-size: 1.75rem; /* 28px mobile */
        }
        
        h2 {
          font-size: 1.5rem; /* 24px mobile */
        }
        
        h3 {
          font-size: 1.25rem; /* 20px mobile */
        }
        
        /* Mobile-first button and link styles with WCAG compliance */
        button, .btn, a[href], Link {
          min-height: 44px; /* WCAG minimum touch target */
          min-width: 44px;
          font-size: 16px; /* Minimum readable size */
          cursor: pointer;
          transition: all 0.3s ease;
          font-weight: 600;
        }
        
        /* Skip Navigation - WCAG AAA */
        .skip-nav {
          background: var(--ember-fire);
          color: white;
          text-decoration: none;
          border-radius: 4px;
          font-weight: 600;
          font-size: 14px;
          transition: all 0.3s ease;
          z-index: 999999;
        }

        .skip-nav:focus {
          outline: 3px solid #FFC107;
          outline-offset: 3px;
        }
        
        /* Screen reader only content */
        .sr-only {
          position: absolute !important;
          width: 1px !important;
          height: 1px !important;
          padding: 0 !important;
          margin: -1px !important;
          overflow: hidden !important;
          clip: rect(0, 0, 0, 0) !important;
          white-space: nowrap !important;
          border: 0 !important;
        }
        
        /* Performance optimizations */
        img {
          content-visibility: auto;
          contain-intrinsic-size: 400px 300px;
          loading: lazy;
        }
        
        /* Float animation for background elements */
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        /* Mobile-first grid and spacing */
        .grid {
          gap: 1rem; /* Mobile base gap */
        }
        
        .px-6 {
          padding-left: 1rem;
          padding-right: 1rem;
        }
        
        /* Tablet styles - 640px and up */
        @media (min-width: 640px) {
          section {
            padding: 3rem 0;
          }
          
          .grid {
            gap: 1.5rem;
          }
          
          .px-6 {
            padding-left: 1.5rem;
            padding-right: 1.5rem;
          }
          
          h1 {
            font-size: 2.25rem; /* 36px tablet */
          }
          
          h2 {
            font-size: 1.875rem; /* 30px tablet */
          }
          
          h3 {
            font-size: 1.5rem; /* 24px tablet */
          }
          
          button, .btn, a[href] {
            font-size: 18px;
          }
        }
        
        /* Desktop styles - 1024px and up */
        @media (min-width: 1024px) {
          section {
            padding: 4rem 0;
          }
          
          .grid {
            gap: 2rem;
          }
          
          h1 {
            font-size: 3rem; /* 48px desktop */
          }
          
          h2 {
            font-size: 2.25rem; /* 36px desktop */
          }
          
          h3 {
            font-size: 1.875rem; /* 30px desktop */
          }
        }
        
        /* Large desktop - 1440px and up */
        @media (min-width: 1440px) {
          h1 {
            font-size: 4rem; /* 64px large desktop */
          }
        }
        
        /* High contrast mode support - WCAG AAA */
        @media (prefers-contrast: high) {
          :root {
            --text-primary: #000000;
            --text-secondary: #000000;
            --ember-fire: #CC0000;
          }
          
          button, input, a, .bg-white {
            border: 2px solid currentColor !important;
          }
          
          .bg-white {
            background: #FFFFFF !important;
            color: #000000 !important;
          }
        }
        
        /* Reduced motion support - WCAG AAA */
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
            scroll-behavior: auto !important;
          }
          
          .animate-float {
            animation: none !important;
          }
        }
        
        /* Print styles */
        @media print {
          .skip-nav,
          button,
          .btn {
            display: none !important;
          }
          
          body {
            background: white !important;
            color: black !important;
          }
          
          * {
            background: white !important;
            color: black !important;
          }
        }
        
        /* Focus visible for modern browsers */
        button:focus-visible,
        a:focus-visible {
          outline: 3px solid #FFC107;
          outline-offset: 3px;
        }
        
        /* Enhanced accessibility for keyboard users */
        .skip-nav:focus,
        button:focus,
        input:focus,
        a:focus {
          z-index: 999999;
        }
        
        /* Error boundary styling */
        .error-boundary {
          padding: 2rem;
          text-align: center;
          background: #FFF3E0;
          border: 1px solid #FFB74D;
          border-radius: 8px;
          margin: 1rem;
        }
        
        /* Mobile-specific improvements */
        @media (max-width: 640px) {
          h1, h2, h3 {
            line-height: 1.2 !important;
          }
          
          /* Improve touch targets on mobile */
          button, a[role="button"] {
            min-height: 48px;
            min-width: 48px;
          }
          
          .px-6 {
            padding-left: 1rem !important;
            padding-right: 1rem !important;
          }
        }
      `}</style>

      <Navigation />
      
      <main 
        id="main-content" 
        role="main"
      >
      
      {/* Hero Section */}
      <section className="pt-20 pb-12 sm:pb-16 bg-gradient-to-br from-neutral-warm via-white to-neutral-warm relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-40 h-40 bg-ember-spark rounded-full blur-3xl animate-float"></div>
          <div className="absolute bottom-20 left-20 w-32 h-32 bg-ember-depth rounded-full blur-3xl animate-float" style={{animationDelay: '3s'}}></div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-neutral-charcoal mb-4 sm:mb-6 leading-tight">
                About <span className="bg-ember-gradient bg-clip-text text-transparent">EMBER</span>
              </h1>
              <p className="text-lg sm:text-xl text-neutral-medium mb-6 sm:mb-8 leading-relaxed">
                We exist because the world doesn't need more managers—it needs more humans who have done 
                the deep work to lead with both fire and heart.
              </p>
              
              <blockquote className="text-xl sm:text-2xl font-light italic text-neutral-dark mb-6 sm:mb-8 pl-4 sm:pl-6 border-l-4 border-ember-fire">
                "Leadership isn't a title you earn—it's a flame you tend."
              </blockquote>

              <p className="text-base sm:text-lg text-neutral-medium leading-relaxed">
                EMBER has grown from a simple belief into a global community of leaders 
                committed to authentic growth and transformational impact.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <img 
                src="https://images.unsplash.com/photo-1513759565286-20e9c5fad06b"
                alt="EMBER mission and vision - leaders in authentic community"
                className="rounded-2xl shadow-2xl w-full"
                loading="lazy"
                width="600"
                height="400"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-ember-spark/20 to-ember-fire/20 rounded-2xl"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-neutral-charcoal mb-8">Our Mission</h2>
            <div className="max-w-5xl mx-auto">
              <p className="text-xl text-neutral-medium leading-relaxed mb-6">
                EMBER's mission is to spark and develop exceptional leadership, empowering people to realize their full potential and create lasting impact. We empower individuals to cultivate emotional maturity and resilience, master their inner and interpersonal dynamics, build peer networks of trusted advisors, embody their strongest values in action, and rise to their highest calling with awakened purpose.
              </p>
              <p className="text-xl text-neutral-medium leading-relaxed">
                Through transformative pathways, participants gain the clarity and capacity to navigate life's challenges, forging positive change that enriches their own lives alongside the families, organizations, and communities they serve.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section id="team-section" className="py-20 bg-gradient-to-br from-neutral-warm to-white" aria-labelledby="values-heading">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 id="values-heading" className="text-4xl font-bold text-neutral-charcoal mb-6">Our Core Values</h2>
            <p className="text-xl text-neutral-medium max-w-3xl mx-auto">
              These values guide everything we do—from how we design our programs to how we show up for each other.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
                  whileHover={{ y: -5 }}
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-ember-fire to-ember-heat rounded-xl mb-4 flex items-center justify-center">
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-neutral-charcoal mb-3">{value.title}</h3>
                  <p className="text-neutral-medium leading-relaxed">{value.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-gradient-to-br from-ember-depth to-ember-cool text-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-6">Our Journey</h2>
            <p className="text-xl opacity-90">From a spark of an idea to a global community of transformed leaders</p>
          </motion.div>

          <div className="space-y-12">
            {milestones.map((milestone, index) => (
              <motion.div
                key={milestone.year}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`flex items-center ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} flex-col lg:text-left text-center`}
              >
                <div className="lg:w-1/2 mb-8 lg:mb-0">
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                    <div className="text-ember-spark font-bold text-2xl mb-2">{milestone.year}</div>
                    <h3 className="text-2xl font-bold mb-4">{milestone.title}</h3>
                    <p className="text-lg opacity-90 leading-relaxed">{milestone.description}</p>
                  </div>
                </div>
                <div className="lg:w-1/2 flex justify-center">
                  <div className="w-4 h-4 bg-ember-spark rounded-full"></div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-neutral-charcoal mb-8">
              Ready to Join Our Community?
            </h2>
            <p className="text-xl text-neutral-medium mb-12 leading-relaxed">
              Whether you're just starting your leadership journey or looking to deepen your impact, 
              there's a place for you in the EMBER community.
            </p>

            <div className="max-w-2xl mx-auto">
              <EmailSignup 
                title=""
                subtitle=""
                buttonText="Subscribe"
                placeholder="Enter your email"
                variant="inline"
                className="justify-center"
              />
            </div>
          </motion.div>
        </div>
      </section>
      </main>

      <Footer />
    </>
  );
};

export default AboutPage;